import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface OfferStats {
  totalOffers: number;
  acceptedOffers: number;
  pendingOffers: number;
  totalClients: number;
}

export const useOfferStats = () => {
  const [stats, setStats] = useState<OfferStats>({
    totalOffers: 0,
    acceptedOffers: 0,
    pendingOffers: 0,
    totalClients: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pobierz statystyki z bazy danych
  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Pobierz liczbę wszystkich ofert
      const { count: totalOffers, error: totalError } = await supabase
        .from('offers')
        .select('*', { count: 'exact', head: true });
      
      if (totalError) throw totalError;

      // Pobierz liczbę zaakceptowanych ofert
      const { count: acceptedOffers, error: acceptedError } = await supabase
        .from('offers')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'accepted');
      
      if (acceptedError) throw acceptedError;

      // Pobierz liczbę oczekujących ofert
      const { count: pendingOffers, error: pendingError } = await supabase
        .from('offers')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      
      if (pendingError) throw pendingError;

      // Pobierz liczbę klientów
      const { count: totalClients, error: clientsError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true });
      
      if (clientsError) throw clientsError;

      setStats({
        totalOffers: totalOffers || 0,
        acceptedOffers: acceptedOffers || 0,
        pendingOffers: pendingOffers || 0,
        totalClients: totalClients || 0
      });
    } catch (err: any) {
      console.error('Error fetching offer stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resetuj statystyki (usuń wszystkie oferty)
  const resetStats = async () => {
    try {
      // Najpierw usuń pozycje ofert
      const { error: itemsError } = await supabase
        .from('offer_items')
        .delete()
        .gte('created_at', '1900-01-01');
      
      if (itemsError) throw itemsError;

      // Potem usuń oferty
      const { error: offersError } = await supabase
        .from('offers')
        .delete()
        .gte('created_at', '1900-01-01');
      
      if (offersError) throw offersError;

      // Odśwież statystyki
      await fetchStats();
      
      return { success: true };
    } catch (err: any) {
      console.error('Error resetting stats:', err);
      return { success: false, error: err.message };
    }
  };

  // Pobierz statystyki przy starcie
  useEffect(() => {
    fetchStats();
  }, []);

  // Ustaw interwał odświeżania co 30 sekund
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return {
    stats,
    loading,
    error,
    refreshStats: fetchStats,
    resetStats
  };
};
